import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, CalendarIcon } from "lucide-react";
import { useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatDateLocal } from "@/shared/lib/date-time-performance";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useEventQuery, useRehearsalSummaryQuery } from "../api/queries";
import { EVENT_KIND_LABELS, eventDisplayCode } from "../lib/event-detail-ui";
import { EventDetailPageSkeleton } from "../ui/EventDetailPageSkeleton";
import { EventDetailPermissionNotice } from "../ui/EventDetailPermissionNotice";
import { EventRehearsalSchedule } from "../ui/EventRehearsalSchedule";
import { EventRehearsalSummary } from "../ui/EventRehearsalSummary";

export function EventRehearsalPage() {
  const { id } = useParams({ from: "/app/performance/events/$id/rehearsals" });
  const nav = useNavigate({ from: "/app/performance/events/$id/rehearsals" });
  const { data: event, isLoading } = useEventQuery(id);
  const canReadAssignments = useActionPermission([
    "performance-assignments:read",
  ]);
  const canManage = useActionPermission(["performance-assignments:update"]);
  const canBookStudio = useActionPermission(["room-bookings:create"]);

  const eventKind = event?.eventKind ?? "stage_show";
  const rehearsalSummaryQuery = useRehearsalSummaryQuery(
    id,
    !!event && canReadAssignments,
  );

  const eventPerformers = useMemo(() => {
    const assignments = event?.assignments ?? [];
    const map = new Map<string, string>();
    for (const a of assignments) {
      if (!map.has(a.performerId)) {
        map.set(a.performerId, a.performerName ?? a.performerId);
      }
    }
    return [...map.entries()]
      .map(([performerId, name]) => ({ id: performerId, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [event?.assignments]);

  if (isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ງານສະແດງ", to: "/app/performance/events" },
              { label: "..." },
            ]}
          />
        </Header>
        <Main>
          <EventDetailPageSkeleton />
        </Main>
      </>
    );
  }

  if (!event) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ງານສະແດງ", to: "/app/performance/events" },
              { label: "ບໍ່ພົບ" },
            ]}
          />
        </Header>
        <Main>
          <p className="text-destructive">ບໍ່ພົບງານສະແດງ</p>
        </Main>
      </>
    );
  }

  const eventDateLabel = event.eventDate
    ? formatDateLocal(event.eventDate)
    : null;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ງານສະແດງ", to: "/app/performance/events" },
            {
              label: event.title,
              to: "/app/performance/events/$id",
              params: { id },
            },
            { label: "ລາຍການວັນຊ້ອມ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
              <CalendarIcon className="size-3.5 text-primary" />
              {eventDisplayCode(event.id)}
              <span>·</span>
              {EVENT_KIND_LABELS[eventKind]}
            </div>
            <h2 className="font-bold text-2xl tracking-tight">ລາຍການວັນຊ້ອມ</h2>
            <p className="mt-1 text-muted-foreground">{event.title}</p>
            {eventDateLabel && (
              <p className="mt-1 text-muted-foreground text-sm">
                ວັນງານ: {eventDateLabel} — ສະຖານະ «ສຳເລັດ» ເທົ່ານັ້ນທີ່ນັບຄ່າຕົວ
              </p>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() =>
              nav({
                to: "/app/performance/events/$id",
                params: { id },
                search: { section: "team" },
              })
            }
          >
            <ArrowLeftIcon className="size-4" />
            ກັບໄປຈັດທີມ
          </Button>
        </div>

        {!canReadAssignments ? (
          <EventDetailPermissionNotice
            title="ບໍ່ມີສິດເບິ່ງລາຍການວັນຊ້ອມ"
            description="ຕ້ອງມີສິດ performance-assignments:read ເພື່ອເບິ່ງການຈັດທີມ ແລະ ວັນຊ້ອມ"
          />
        ) : (
          <>
            <div className="mb-4 overflow-hidden rounded-xl border bg-card">
              <EventRehearsalSummary
                show
                summary={rehearsalSummaryQuery.data}
                isLoading={rehearsalSummaryQuery.isLoading}
              />
            </div>

            <EventRehearsalSchedule
              eventId={id}
              eventDate={event.eventDate}
              eventPerformers={eventPerformers}
              defaultRoomId={undefined}
              defaultRoomName={undefined}
              canManage={canManage}
              canPrint={canReadAssignments}
              canBookStudio={canBookStudio}
              hideSectionHeader
            />
          </>
        )}
      </Main>
    </>
  );
}
